import React, { useEffect, useState } from 'react';
import {
  Plus,
  Trash2,
  Pencil,
  Check,
  X,
  CreditCard,
  Upload,
  FileText,
  Percent,
  Home,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { cn } from '../../lib/utils';
import paymentMethodService from '../../services/paymentMethod.service';
import mercadopagoService from '../../services/mercadopago.service';

const EMPTY_FORM = {
  method: '',
  details: '',
  active: false,
  order: 0,
  usesPaymentGateway: false,
  uploadAttachment: false,
  invoiceNumRequired: false,
  chargePercent: 0,
  chargePayer: '',
  showInHome: false,
  homeDescription: '',
};

/** Small flag chip */
function FlagChip({ icon: Icon, label, active }) {
  if (!active) return null;
  return (
    <span className='inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary'>
      {Icon && <Icon className='h-2.5 w-2.5' />}
      {label}
    </span>
  );
}

/** Checkbox row */
function CheckRow({ id, checked, onChange, label, hint }) {
  return (
    <div className='flex items-start gap-2.5'>
      <input
        id={id}
        type='checkbox'
        checked={checked}
        onChange={e => onChange(e.target.checked)}
        className='mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded border-input accent-primary'
      />
      <div>
        <label htmlFor={id} className='cursor-pointer text-sm font-medium text-foreground'>
          {label}
        </label>
        {hint && <p className='text-xs text-muted-foreground'>{hint}</p>}
      </div>
    </div>
  );
}

export default function PaymentMethodConfig() {
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [hasMpKey, setHasMpKey] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const [data, mpKeyData] = await Promise.all([
        paymentMethodService.getAllMethods(),
        mercadopagoService.getAccessToken().catch(() => ({ token: '' })),
      ]);
      setMethods(data);
      setHasMpKey(Boolean(mpKeyData?.token?.length));
    } catch {
      toast.error('Error al cargar métodos de pago');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const patch = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const resetForm = () => setForm(EMPTY_FORM);

  const handleEdit = m => {
    setForm({
      method: m.method || '',
      details: m.details || '',
      active: m.active ?? false,
      order: m.order ?? 0,
      usesPaymentGateway: m.usesPaymentGateway ?? false,
      uploadAttachment: m.uploadAttachment ?? false,
      invoiceNumRequired: m.invoiceNumRequired ?? false,
      chargePercent: m.chargePercent ?? 0,
      chargePayer: m.chargePayer || '',
      showInHome: m.showInHome ?? false,
      homeDescription: m.homeDescription || '',
    });
    setEditingId(m._id);
    setShowForm(true);
  };

  const isMpMethod = name =>
    ['mercadopago', 'mercadopagocuotas'].includes((name || '').toLowerCase());

  const handleSave = async () => {
    if (!form.method.trim()) return toast.error('El nombre es requerido');

    // Block creating duplicate MP methods manually
    if (!editingId && isMpMethod(form.method)) {
      return toast.error(
        'Mercado Pago está integrado por defecto. Editá el método existente si necesitás cambiar algo.'
      );
    }

    // Warn if trying to activate MP without key
    if (form.active && isMpMethod(form.method) && !hasMpKey) {
      return toast.error(
        'Configurá el Access Token de Mercado Pago (sección "Mercado Pago") antes de activar este método.'
      );
    }

    setSaving(true);
    try {
      if (editingId) {
        await paymentMethodService.updateMethod(editingId, form);
        toast.success('Método actualizado');
      } else {
        await paymentMethodService.createMethod(form);
        toast.success('Método creado');
      }
      setShowForm(false);
      setEditingId(null);
      resetForm();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async id => {
    if (!window.confirm('¿Eliminar este método de pago?')) return;
    try {
      await paymentMethodService.deleteMethod(id);
      toast.success('Eliminado');
      load();
    } catch {
      toast.error('Error al eliminar');
    }
  };

  const toggleActive = async m => {
    if (!m.active && isMpMethod(m.method) && !hasMpKey) {
      return toast.error('Configurá el Access Token de Mercado Pago antes de activar este método.');
    }
    try {
      await paymentMethodService.updateMethod(m._id, { active: !m.active });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Error al actualizar');
    }
  };

  return (
    <div className='space-y-4'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <p className='text-sm text-muted-foreground'>
          Configurá los métodos de pago del checkout. El campo <strong>Detalles</strong> se muestra
          al comprador cuando selecciona este método.
        </p>
        <Button
          size='sm'
          onClick={() => {
            resetForm();
            setEditingId(null);
            setShowForm(prev => !prev);
          }}
        >
          <Plus className='mr-1.5 h-4 w-4' /> Nuevo método
        </Button>
      </div>

      {/* MP key warning */}
      {!hasMpKey && (
        <div className='rounded-lg border border-yellow-200 bg-yellow-50 px-3 py-2.5 text-xs text-yellow-800'>
          ⚠️ No hay Access Token de Mercado Pago configurado. Para activar métodos de pasarela,
          configuralo en la sección <strong>Mercado Pago</strong>.
        </div>
      )}

      {/* Add / Edit form */}
      {showForm && (
        <div className='rounded-xl border border-border bg-card p-5 space-y-4 shadow-sm'>
          <p className='text-sm font-semibold text-foreground'>
            {editingId ? 'Editar método' : 'Nuevo método de pago'}
          </p>

          <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
            <div className='space-y-1'>
              <label className='block text-xs font-medium text-foreground'>Nombre *</label>
              <Input
                value={form.method}
                onChange={e => patch('method', e.target.value)}
                placeholder='Ej: Transferencia bancaria'
              />
            </div>
            <div className='space-y-1'>
              <label className='block text-xs font-medium text-foreground'>
                Orden de visualización
              </label>
              <Input
                type='number'
                value={form.order}
                onChange={e => patch('order', Number(e.target.value))}
                placeholder='0'
              />
            </div>
          </div>

          <div className='space-y-1'>
            <label className='block text-xs font-medium text-foreground'>
              Detalles / Instrucciones de pago
            </label>
            <textarea
              value={form.details}
              onChange={e => patch('details', e.target.value)}
              rows={4}
              placeholder={
                'Ej:\nBanco Santander\nCuenta corriente: 1234567890\nTitular: Club Deportivo\n\nUna vez realizada la transferencia, adjuntá el comprobante.'
              }
              className='w-full resize-y rounded-xl border border-input bg-background px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
            />
            <p className='text-xs text-muted-foreground'>
              Este texto se muestra al comprador cuando selecciona este método en el checkout.
            </p>
          </div>

          {/* Surcharge */}
          <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
            <div className='space-y-1'>
              <label className='block text-xs font-medium text-foreground'>
                Recargo (%) — 0 para sin recargo
              </label>
              <Input
                type='number'
                min={0}
                step={0.1}
                value={form.chargePercent}
                onChange={e => patch('chargePercent', Number(e.target.value))}
                placeholder='0'
              />
            </div>
            <div className='space-y-1'>
              <label className='block text-xs font-medium text-foreground'>
                ¿Quién paga el recargo?
              </label>
              <select
                value={form.chargePayer}
                onChange={e => patch('chargePayer', e.target.value)}
                className='w-full rounded-xl border border-input bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
              >
                <option value=''>Sin recargo / no aplica</option>
                <option value='client'>Cliente (se suma al total)</option>
                <option value='store'>Comercio (absorbido)</option>
              </select>
            </div>
          </div>

          {/* Checkout flags */}
          <div className='rounded-lg border border-border bg-muted/30 p-3 space-y-3'>
            <p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
              Comportamiento en el checkout
            </p>
            <CheckRow
              id='pm-active'
              checked={form.active}
              onChange={v => patch('active', v)}
              label='Activo (visible en checkout)'
            />
            <CheckRow
              id='pm-gateway'
              checked={form.usesPaymentGateway}
              onChange={v => patch('usesPaymentGateway', v)}
              label='Usa pasarela de pago (Mercado Pago)'
              hint='El checkout redirige al comprador al flujo de pago de la pasarela en lugar de mostrar instrucciones manuales.'
            />
            <CheckRow
              id='pm-upload'
              checked={form.uploadAttachment}
              onChange={v => patch('uploadAttachment', v)}
              label='Requiere comprobante adjunto'
              hint='Se muestra un campo para subir el comprobante de pago antes de confirmar.'
            />
            <CheckRow
              id='pm-invoice'
              checked={form.invoiceNumRequired}
              onChange={v => patch('invoiceNumRequired', v)}
              label='Requiere número de transacción / comprobante'
              hint='Se solicita al comprador el número de operación o comprobante.'
            />
          </div>

          {/* Homepage visibility */}
          <div className='rounded-lg border border-border bg-muted/30 p-3 space-y-3'>
            <p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
              Página de inicio
            </p>
            <CheckRow
              id='pm-show-home'
              checked={form.showInHome}
              onChange={v => patch('showInHome', v)}
              label='Mostrar en la página de inicio'
              hint='Aparece como tarjeta en la sección "Métodos de pago" del inicio.'
            />
            {form.showInHome && (
              <div className='space-y-1 pt-1'>
                <label className='block text-xs font-medium text-foreground'>
                  Descripción para el inicio
                </label>
                <textarea
                  value={form.homeDescription}
                  onChange={e => patch('homeDescription', e.target.value)}
                  rows={2}
                  placeholder='Ej: Transferencia bancaria sin costo adicional.'
                  className='w-full resize-y rounded-xl border border-input bg-background px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                />
                <p className='text-xs text-muted-foreground'>
                  Texto breve visible en la tarjeta de la página de inicio.
                </p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className='flex gap-2 pt-1'>
            <Button onClick={handleSave} disabled={saving} size='sm'>
              <Check className='mr-1 h-4 w-4' />
              {saving ? 'Guardando…' : 'Guardar'}
            </Button>
            <Button
              variant='outline'
              size='sm'
              onClick={() => {
                setShowForm(false);
                setEditingId(null);
                resetForm();
              }}
            >
              <X className='mr-1 h-4 w-4' /> Cancelar
            </Button>
          </div>
        </div>
      )}

      {/* Methods list */}
      {loading ? (
        <p className='text-sm text-muted-foreground'>Cargando…</p>
      ) : methods.length === 0 ? (
        <p className='text-sm text-muted-foreground'>No hay métodos configurados aún.</p>
      ) : (
        <ul className='space-y-2'>
          {methods.map(m => (
            <li
              key={m._id}
              className='overflow-hidden rounded-xl border border-border bg-card shadow-sm'
            >
              {/* Header row */}
              <div className='flex items-center gap-3 px-4 py-3'>
                <div className='flex-1 min-w-0 space-y-1'>
                  <div className='flex flex-wrap items-center gap-2'>
                    <span className='text-sm font-semibold text-foreground'>{m.method}</span>
                    <span
                      className={cn(
                        'rounded-full px-1.5 py-0.5 text-[10px] font-medium',
                        m.active ? 'bg-green-100 text-green-700' : 'bg-muted text-muted-foreground'
                      )}
                    >
                      {m.active ? 'Activo' : 'Inactivo'}
                    </span>
                    <FlagChip icon={Home} label='Inicio' active={m.showInHome} />
                    <FlagChip icon={CreditCard} label='Pasarela' active={m.usesPaymentGateway} />
                    <FlagChip icon={Upload} label='Comprobante' active={m.uploadAttachment} />
                    <FlagChip
                      icon={FileText}
                      label='Nro. transacción'
                      active={m.invoiceNumRequired}
                    />
                    {m.chargePercent > 0 && (
                      <span className='inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-medium text-orange-700'>
                        <Percent className='h-2.5 w-2.5' />
                        {m.chargePercent}% {m.chargePayer === 'client' ? '(cliente)' : ''}
                      </span>
                    )}
                  </div>
                  {m.details && (
                    <p className='truncate text-xs text-muted-foreground'>{m.details}</p>
                  )}
                </div>

                {/* Action buttons */}
                <div className='ml-2 flex shrink-0 items-center gap-1'>
                  <Button
                    variant='ghost'
                    size='icon-sm'
                    onClick={() => toggleActive(m)}
                    title={m.active ? 'Desactivar' : 'Activar'}
                  >
                    {m.active ? (
                      <X className='h-3.5 w-3.5 text-muted-foreground' />
                    ) : (
                      <Check className='h-3.5 w-3.5 text-green-600' />
                    )}
                  </Button>
                  <Button variant='ghost' size='icon-sm' onClick={() => handleEdit(m)}>
                    <Pencil className='h-3.5 w-3.5' />
                  </Button>
                  <Button
                    variant='ghost'
                    size='icon-sm'
                    onClick={() => handleDelete(m._id)}
                    disabled={isMpMethod(m.method)}
                    title={
                      isMpMethod(m.method)
                        ? 'Los métodos de Mercado Pago no pueden eliminarse'
                        : 'Eliminar'
                    }
                  >
                    <Trash2
                      className={`h-3.5 w-3.5 ${isMpMethod(m.method) ? 'text-muted-foreground/40' : 'text-destructive'}`}
                    />
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
